import { z } from "zod";
import {
  MEMBER_DUE_STATUS,
  PAYMENT_CONFIRMATION_STATUS,
} from "./collections.js";
// US-E16.F6-0187 (@UC9249): chính sách overpay hiệu lực trả kèm response record-paid (consumer THẬT).
import { overpayCreditPolicySchema } from "./collection-config.js";
// US-E19.F1-0200 (@UC9296): khoá idempotency NGHIỆP VỤ (1 nguồn — dùng chung record-paid + correction).
import { businessIdempotencyKeySchema } from "./ledger-correction.js";
import { evidenceKeySchema } from "./contribution-evidence.js";

/* ===========================================================================
 * US-E6.F3-0041 — Approve / Decline Payment Confirmation (@UC0040 / @UC0051)
 * Phase 6 Ledger core + Phase 7 Payments — @gate money,ledger.
 *
 * REST (PO-chốt 01/07): resource-oriented trên payment_confirmation.
 *  - GET  /v1/collections/:id/dues?status=pending      — list báo CK chờ duyệt (cursor)
 *  - POST /v1/payment-confirmations/:id/approve         — duyệt → post CONTRIBUTION + due=paid
 *  - POST /v1/payment-confirmations/:id/decline         — từ chối (reason) → due=pending, KHÔNG ledger
 *
 * B1: mọi số tiền = STRING minor unit (BigInt, KHÔNG float).
 * ========================================================================= */

/** Chuỗi số nguyên minor unit (BigInt-as-STRING) — chống mất chính xác >2^53. */
const MONEY_INTEGER_STRING_PATTERN = /^\d+$/;

/** confirmationSource cho CONTRIBUTION (khớp LedgerTransaction.confirmationSource). */
export const CONFIRMATION_SOURCE = {
  TWO_PARTY: "two_party",
  ADMIN_ONE_PARTY: "admin_one_party",
  GATEWAY_AUTO: "gateway_auto",
  /**
   * 🔴 US-E6.F3-0582 (@UC0057 · review #650 HIGH-2) — NGƯỜI NỘP TỰ KHAI, MỘT BÊN, KHÔNG AI ĐỐI CHIẾU.
   *
   * Cờ `self_payment_auto_confirm` BẬT ⇒ member/phụ huynh tự khai cho chính suất mình và bút toán
   * vào sổ ngay. Bản đầu của 0582 ghi `two_party` cho đường này — **sai sự thật**: `two_party` nghĩa
   * là *hai bên* (người khai + người xác nhận), mà ở đây chỉ có một.
   *
   * Vì sao đáng một giá trị riêng chứ không mượn `admin_one_party`: cột này nằm trên
   * `ledger_transactions` — bảng **APPEND-ONLY**. Nhãn sai ghi vào đó thì **không sửa được nữa**, và
   * nó chính là thứ người đối chiếu sổ dựa vào để biết một khoản thu đã được ai xác nhận. Mượn nhãn
   * của admin còn tệ hơn `two_party`: nó gán cho một quản trị viên hành động họ không hề làm.
   */
  SELF_ONE_PARTY: "self_one_party",
  /**
   * 🔴 US-AE4.F7-1040 (@UC9953 · QĐ người dùng 30/10/2026) — NHÂN VIÊN GOPPY xác nhận hộ (duyệt hộ lời khai ở trang admin, sửa nhầm từ
   * admin). Bút toán mang nhãn này có `createdBy` NULL ở đường duyệt hộ: nhân viên không phải user của nhóm, danh tính thật nằm ở
   * `audit_log` của admin. Không đứng tên owner — làm vậy là gán cho owner một lần duyệt họ không làm, trên bảng append-only.
   * Khoản nợ quỹ: nhân viên là BÊN THỨ BA (không bao giờ là người nợ).
   */
  SYSTEM_ADMIN_ASSIST: "system_admin_assist",
  /**
   * AI (US-E11.F1-0064-S03 · @UC0072): bút toán do trợ lý AI tạo SAU khi người dùng Xác nhận
   * proposal (human-in-the-loop). Tag này để admin ai-audit trace nối idempotencyKey chat→ledger.
   * AI KHÔNG BAO GIỜ ghi ledger trực tiếp — chỉ qua domain engine (LedgerService/consensus/collection).
   */
  AI: "ai",
} as const;

/**
 * US-AE4.F7-1040 (@UC9953) — tên hiển thị của người làm khi việc do NHÂN VIÊN Goppy làm hộ (bút toán `system_admin_assist`, lời khai được
 * duyệt hộ). Không phát tên nhân viên ra ngoài; một chuỗi cho mọi màn admin.
 */
export const SUPPORT_TEAM_DISPLAY_NAME = "Đội hỗ trợ Goppy";

export type ConfirmationSource =
  (typeof CONFIRMATION_SOURCE)[keyof typeof CONFIRMATION_SOURCE];

/**
 * 🔴 US-E6.F3-0582 (review #650 HIGH-A) — DANH SÁCH NÀY PHẢI PHỦ HẾT `CONFIRMATION_SOURCE`.
 *
 * Đây là **whitelist ĐÔI** cho cùng một tập giá trị, và repo đã có tiền lệ drift đúng kiểu này ở
 * `txType`. Bản đầu của 0582 thêm `SELF_ONE_PARTY` vào hằng số mà quên ở đây ⇒ chính parser chuẩn
 * của cột `ledger_transactions.confirmation_source` **từ chối giá trị mà engine vừa ghi vào cột đó**,
 * và `approve()` (public, kiểu `ConfirmationSource`) nhận `self_one_party` rồi nổ ZodError **SAU KHI
 * tx đã commit** — tiền đã vào sổ, response 500.
 *
 * Dựng bằng `Object.values` thay vì chép tay: giá trị thứ sáu thêm vào hằng số là **tự có mặt**,
 * không phụ thuộc ai nhớ. `parity` test bên cạnh giữ vế còn lại (danh sách không rỗng, không thừa).
 */
export const confirmationSourceSchema = z.enum(
  Object.values(CONFIRMATION_SOURCE) as [string, ...string[]],
);

/* ---------------------------------------------------------------------------
 * GET /v1/collections/:id/dues?status=pending — danh sách báo CK chờ duyệt (AC1)
 * ------------------------------------------------------------------------- */

export const pendingDueItemSchema = z
  .object({
    confirmationId: z.string().uuid(),
    memberDueId: z.string().uuid(),
    memberId: z.string().uuid(),
    memberName: z.string().nullable(),
    claimedAmountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    proofUrl: z.string().url().nullable(),
    submittedAt: z.string().datetime(),
  })
  .strict();

export type PendingDueItem = z.infer<typeof pendingDueItemSchema>;

export const listPendingDuesResponseSchema = z
  .object({
    dues: z.array(pendingDueItemSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();

export type ListPendingDuesResponse = z.infer<
  typeof listPendingDuesResponseSchema
>;

/* ---------------------------------------------------------------------------
 * POST /v1/payment-confirmations/:id/approve — duyệt (AC2)
 * ------------------------------------------------------------------------- */

export const approvePaymentResponseSchema = z
  .object({
    confirmationId: z.string().uuid(),
    memberDueId: z.string().uuid(),
    ledgerTxId: z.string().uuid(),
    confirmationSource: confirmationSourceSchema,
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    /**
     * 🔴 NỚI TỪ `z.literal(PAID)` SANG HAI GIÁ TRỊ (QĐ người dùng 01/09/2026).
     *
     * Lời khai nay mang số THỰC ĐÓNG (xem `ReportPaymentService`), nên duyệt một lời khai 100 trên
     * nghĩa vụ 500 để lại khoản ở `partially_paid` — còn thiếu 400. Ép `literal(PAID)` ở đây buộc
     * server hoặc nói dối, hoặc ném lỗi parse sau khi ĐÃ ghi bút toán (tiền vào sổ rồi mới 500).
     *
     * Vẫn KHÔNG mở cho mọi `MemberDueStatus`: duyệt chỉ đẻ ra được hai kết cục này. `waived`,
     * `overdue`, `awaiting_confirmation` xuất hiện ở đây là bất biến đã vỡ — giữ union hẹp để
     * trạng thái sai KHÔNG BIỂU DIỄN ĐƯỢC (§7 contract `.strict()`).
     */
    newDueStatus: z.enum([
      MEMBER_DUE_STATUS.PAID,
      MEMBER_DUE_STATUS.PARTIALLY_PAID,
    ]),
    newConfirmationStatus: z.literal(PAYMENT_CONFIRMATION_STATUS.APPROVED),
    approvedAt: z.string().datetime(),
  })
  .strict();

export type ApprovePaymentResponse = z.infer<
  typeof approvePaymentResponseSchema
>;

/* ---------------------------------------------------------------------------
 * POST /v1/payment-confirmations/:id/decline — từ chối + reason (AC3)
 * ------------------------------------------------------------------------- */

/** Reason bắt buộc (>=1 ký tự sau trim), tối đa 500 để chống abuse. */
export const declinePaymentRequestSchema = z
  .object({
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export type DeclinePaymentRequest = z.infer<
  typeof declinePaymentRequestSchema
>;

export const declinePaymentResponseSchema = z
  .object({
    confirmationId: z.string().uuid(),
    memberDueId: z.string().uuid(),
    /**
     * 🔴 NỚI TỪ `z.literal(PENDING)` (vá review PR #679 HIGH-2). Từ chối một lời khai trên khoản ĐÃ
     * ĐÓNG DỞ phải trả khoản về `partially_paid` — ghi cứng `PENDING` sẽ xoá phần đã vào sổ cái
     * khỏi mọi bề mặt tiến độ. Union hẹp: từ chối không đẻ ra kết cục nào khác.
     *
     * 🔴 THÊM `waived` rồi `paid_on_behalf` (US-E6.F3-0680, 02/09/2026). Khoản ĐÃ KHÉP thì từ chối
     * một lời khai treo trên nó KHÔNG được đổi status:
     *  · `waived` — dẫn status theo số đã đóng sẽ dựng lại một món nợ nhóm đã tha (§3.6 P4.3);
     *  · `paid_on_behalf` — nộp hộ không đặt `paidAmountMinor`, nên dẫn theo cột đó ra `pending`
     *    ⇒ cổng `record-paid` hết chặn ⇒ CONTRIBUTION thứ hai cho nghĩa vụ đã trả (vòng 4 H-1);
     *  · `paid` — cùng vị-từ đó (`isPaidDueStatus` = {paid, paid_on_behalf}) nên nó là kết cục THỨ
     *    NĂM, và bản trước quên liệt kê ⇒ `.parse` ném `ZodError` (review vòng 5).
     *
     * 🔴 Ca `paid` KHÔNG hiếm và hậu quả không dừng ở một lỗi 500: thủ quỹ bấm "Ghi nhận đã đóng"
     * cho một suất đang chờ duyệt (`record-paid` cho `awaiting_confirmation` qua, và nó KHÔNG đụng
     * `payment_confirmations`) ⇒ due thành `paid` mà lời khai vẫn `submitted`, vẫn nằm trong hàng
     * đợi. Thủ quỹ dọn hàng đợi bằng nút Từ chối ⇒ `ZodError` ⇒ và vì `@Audited` SỞ HỮU transaction
     * (`audit.interceptor.ts:56`), lỗi này ROLLBACK cả lệnh đánh dấu `rejected` ⇒ lời khai **kẹt
     * vĩnh viễn**. Không sai một đồng, nhưng hàng đợi không bao giờ vơi.
     *
     * Union vẫn HẸP — năm kết cục liệt kê tường minh, không phải `MemberDueStatus` mở.
     */
    newDueStatus: z.enum([
      MEMBER_DUE_STATUS.PENDING,
      MEMBER_DUE_STATUS.PARTIALLY_PAID,
      MEMBER_DUE_STATUS.WAIVED,
      MEMBER_DUE_STATUS.PAID_ON_BEHALF,
      MEMBER_DUE_STATUS.PAID,
    ]),
    newConfirmationStatus: z.literal(PAYMENT_CONFIRMATION_STATUS.REJECTED),
    reason: z.string(),
    declinedAt: z.string().datetime(),
  })
  .strict();

export type DeclinePaymentResponse = z.infer<
  typeof declinePaymentResponseSchema
>;

/* ===========================================================================
 * US-E6.F3-0042 — Record-paid (admin_one_party) + bulk + undo (@UC0040)
 * Phase 6 Ledger + Phase 7 — @gate money,ledger. REUSE móng LedgerService 0041.
 * ========================================================================= */

/** Trần PG signed BIGINT (chống overflow P2020→500). */
const RECORD_PAID_PG_BIGINT_MAX = 9223372036854775807n;

/**
 * Body cho POST /v1/member-dues/:id/record-paid.
 *  - `declaredAmountMinor?` (US-E16.F1-0182 · @UC9234 open_amount): CHỈ dùng khi due là open_amount
 *    (amount=0 lúc tạo) — member/admin KHAI số thực đóng. Đợt equal/per_member/tiered/target_split
 *    (due amount>0) BỎ QUA field này (dùng due.amountMinor — hành vi cũ KHÔNG regress).
 *  - STRING số nguyên dương ≤ trần BIGINT (B1 — KHÔNG float). `.strict()` chặn over-post.
 */
const positiveMinorString = (label: string) =>
  z
    .string()
    .trim()
    .regex(MONEY_INTEGER_STRING_PATTERN, `${label} phải là số nguyên (đơn vị nhỏ nhất)`)
    .refine((v) => {
      try {
        const n = BigInt(v);
        return n > 0n && n <= RECORD_PAID_PG_BIGINT_MAX;
      } catch {
        return false;
      }
    }, `${label} phải lớn hơn 0 và không vượt giới hạn`);

export const recordPaidRequestSchema = z
  .object({
    declaredAmountMinor: positiveMinorString("Số tiền").optional(),
    /**
     * US-E16.F4-0185 (@UC9241/@UC9242): số THỰC đóng cho due amount>0 (partial/overpay).
     *  - < còn-nợ ⇒ đóng MỘT PHẦN (due `partially_paid`, còn nợ = due − paid).
     *  - = còn-nợ ⇒ đóng ĐỦ (due `paid`) — tương đương hành vi cũ (KHÔNG regress).
     *  - > còn-nợ ⇒ đóng DƯ (due `paid` + phần dư → advance_credit producer E15.F3).
     * Vắng ⇒ đóng ĐỦ còn-nợ (hành vi cũ). STRING minor dương ≤ trần BIGINT (B1 no-float).
     */
    paidAmountMinor: positiveMinorString("Số tiền thực đóng").optional(),
    /**
     * US-E19.F1-0200 (@UC9296) — KHOÁ NGHIỆP VỤ chống DOUBLE-POST (SEC-DOUBLE-POST).
     *
     * Client CÓ THỂ gửi khoá ỔN ĐỊNH theo lần bấm nút (KHÔNG random mỗi lần retry) ⇒ bấm kép vì lag
     * ⇒ CÙNG khoá ⇒ ledger `unique(fundId, idempotencyKey)` chặn ⇒ ĐÚNG 1 bút toán.
     * VẮNG ⇒ server TỰ DERIVE khoá từ NỘI DUNG hành động (domain `deriveBusinessIdempotencyKey`) ⇒
     * client cũ (không biết field này) VẪN được chống double-post — KHÔNG regress.
     */
    businessIdempotencyKey: businessIdempotencyKeySchema.optional(),
    /** 🔴 US-E6.F3-0910 (@UC0057) — khoá file bằng chứng (upload hai bước). KHÔNG đi vào khoá idempotency. */
    evidenceKey: evidenceKeySchema.optional(),
  })
  .strict();

export type RecordPaidRequest = z.infer<typeof recordPaidRequestSchema>;

/**
 * POST /v1/member-dues/:id/record-paid — ghi nhận 1-đầu (AC1). member_due pending→paid, post
 * CONTRIBUTION (confirmationSource=admin_one_party). Idempotent qua Idempotency-Key + ledger unique.
 *
 * 🔴 FIX-1278 H2 (FUND-DEBT-SELF-CONFIRM · QĐ PO 06/10) — cửa này trả ĐÚNG nhãn đã ghi vào bút toán: quản lý DUY NHẤT tự ghi nhận việc trả
 * khoản nợ quỹ CỦA CHÍNH MÌNH ⇒ `self_one_party` (một bên, không ai đối chiếu). Ghim `admin_one_party` ở đây là làm hợp đồng nói dối về sổ.
 * Chỉ HAI giá trị — cửa HTTP này không bao giờ sinh `two_party`/`gateway`.
 */
export const recordPaidResponseSchema = z
  .object({
    memberDueId: z.string().uuid(),
    ledgerTxId: z.string().uuid(),
    confirmationSource: z.enum([CONFIRMATION_SOURCE.ADMIN_ONE_PARTY, CONFIRMATION_SOURCE.SELF_ONE_PARTY]),
    /** Số CONTRIBUTION posted lần này (partial ⇒ phần đóng; overpay ⇒ khớp còn-nợ due). */
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    /**
     * US-E16.F4-0185 (@UC9241/@UC9242): partial ⇒ `partially_paid`; đóng đủ/dư ⇒ `paid`.
     * (Hành vi cũ đóng-đủ vẫn trả `paid` — KHÔNG regress.)
     */
    newDueStatus: z.enum([MEMBER_DUE_STATUS.PAID, MEMBER_DUE_STATUS.PARTIALLY_PAID]),
    /** Tổng đã đóng tích luỹ cho due sau lần này (partial). STRING minor. */
    paidAmountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Còn nợ sau lần này = amountMinor − paidAmountMinor (0 khi paid). STRING minor. */
    remainingMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Phần dư đã ghi advance_credit (overpay). "0" nếu không dư. STRING minor. */
    advanceCreditMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** DebtEntry advance_credit id (producer E15.F3) nếu có overpay; null nếu không. */
    advanceCreditEntryId: z.string().uuid().nullable(),
    /**
     * US-E16.F6-0187 (@UC9249) — chính sách overpay HIỆU LỰC của quỹ (config `overpay_credit_policy`,
     * fund > group > system; default `credit` ⇒ KHÔNG regress F4). `reject` không bao giờ trả 200.
     */
    overpayPolicy: overpayCreditPolicySchema.default("credit"),
    /**
     * Phần dư CHỜ HOÀN khi policy=`refund` (KHÔNG vào quỹ, KHÔNG thành advance_credit). "0" ở policy
     * khác. STRING minor (B1).
     */
    refundPendingMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN).default("0"),
    recordedAt: z.string().datetime(),
  })
  .strict();

export type RecordPaidResponse = z.infer<typeof recordPaidResponseSchema>;

/**
 * POST /v1/collections/:id/dues/record-paid-all — bulk (AC2). Mỗi due chưa đóng = 1 tx idempotent.
 * Trả số ghi nhận + danh sách memberDueId đã paid.
 */
export const recordPaidBulkResponseSchema = z
  .object({
    recordedCount: z.number().int().nonnegative(),
    recordedMemberDueIds: z.array(z.string().uuid()),
    /**
     * RECORD-PAID-409-LOWS-2 (3) — số khoản BỎ QUA vì sự thật của chính khoản (biến mất `MEMBER_DUE_NOT_FOUND` · đã khép
     * `MEMBER_DUE_NOT_OPEN` · đua khoá `MEMBER_DUE_CONCURRENT_WRITE`); khoản replay (đã ghi từ trước) không tính. Server LUÔN gửi; tuỳ
     * chọn trong hợp đồng để client cũ (flutter-web đọc tay `recordedCount`) không vỡ.
     */
    skippedCount: z.number().int().nonnegative().optional(),
  })
  .strict();

export type RecordPaidBulkResponse = z.infer<
  typeof recordPaidBulkResponseSchema
>;

/**
 * POST /v1/member-dues/:id/undo-record — huỷ ghi nhận (AC3) → REVERSAL append-only + due→pending.
 */
export const undoRecordResponseSchema = z
  .object({
    memberDueId: z.string().uuid(),
    reversalTxId: z.string().uuid(),
    /**
     * 🔴 NỚI TỪ `z.literal(PENDING)` (vá review PR #679 vòng 2). `undo-record` chỉ đảo bút toán của
     * lượt chốt CUỐI; các lượt trước vẫn nằm trong sổ. Khoản đóng làm nhiều lượt sau khi huỷ ghi
     * nhận phải về `partially_paid` — trả `pending` là nói rằng quỹ không giữ đồng nào của khoản
     * này, trong khi nó đang giữ.
     */
    newDueStatus: z.enum([
      MEMBER_DUE_STATUS.PENDING,
      MEMBER_DUE_STATUS.PARTIALLY_PAID,
    ]),
    undoneAt: z.string().datetime(),
  })
  .strict();

export type UndoRecordResponse = z.infer<typeof undoRecordResponseSchema>;

/**
 * 🔴 UNDO-RECORD-409-CODE (@UC0040) — MÃ ỔN ĐỊNH cho 409 của `undo-record` khi khoản KHÔNG ở trạng thái huỷ được: chỉ khoản đã
 * được ghi nhận ĐÓNG ĐỦ (`paid`, có bút toán chốt để đảo) mới huỷ được. Trước đây service ném 409 bằng chuỗi trần ⇒ problem+json
 * mang `code: "CONFLICT"` chung, client không phân biệt được với mọi 409 khác (quỹ đóng băng, nhóm tạm ngưng…).
 *
 * `params.status` = trạng thái HIỆN TẠI của khoản (đọc dưới khoá dòng), giá trị nguyên thuỷ phẳng — client dựng câu theo đó
 * (vd khoản vừa bị sửa thành đóng dở ⇒ `partially_paid`; huỷ lần hai ⇒ `pending`). Ngôn-ngữ-trung-tính: client map
 * `code` (+ `params`) → câu, không đọc `detail`.
 *
 * Khác `DUE_LEDGER_INCONSISTENT` (422, `ERROR_CATALOG`): đó là sổ và cột luỹ kế nói hai con số — dữ liệu hỏng, cần đối chiếu;
 * mã này là thao tác sai lúc, dữ liệu vẫn đúng.
 */
export const MEMBER_DUE_NOT_UNDOABLE_CODE = "MEMBER_DUE_NOT_UNDOABLE" as const;

/**
 * 🔴 IDEMPOTENCY-REPLAY-FIX (@UC0040 · CLAUDE.md §3.4 · QĐ người dùng 05/10/2026 "Chặn, báo rõ") — `record-paid` gửi lại CÙNG nội dung
 * (cùng người, cùng số) cho một lần ghi mà bút toán gốc đã bị ĐẢO bởi SỬA NHẦM (không phải huỷ ghi nhận). Lần gửi lại KHÔNG replay (đó là
 * "đã ghi" mà sổ không có) và KHÔNG chạy (chạy = âm thầm huỷ lần sửa: ghi lại đủ số cũ + đóng dư thành ứng trước). 0 bút toán. Lối ra:
 * nhập số CÒN THIẾU để ghi tiếp. Không `params`.
 */
export const MEMBER_DUE_CORRECTED_CODE = "MEMBER_DUE_CORRECTED" as const;

/**
 * 🔴 IDEMPOTENCY-REPLAY-FIX (review #1231 M1) — ghi lại sau HUỶ GHI NHẬN một lần đóng DƯ (policy `credit`): lần ghi gốc ĐÃ cấp ứng trước cho
 * phần dư và huỷ ghi nhận KHÔNG thu hồi nó ⇒ cấp lần hai là đẻ tiền. 409, cả tx rollback, 0 bút toán. Trước: 409 chuỗi trần của producer
 * ("đang được ghi bởi một yêu cầu khác" — không có yêu cầu nào khác). Thu hồi ứng trước khi huỷ: việc riêng UNDO-RECORD-OVERPAY-CREDIT.
 */
export const ADVANCE_CREDIT_ALREADY_GRANTED_CODE = "ADVANCE_CREDIT_ALREADY_GRANTED" as const;

/**
 * 🔴 UNDO-RECORD-OVERPAY-CREDIT-FIX (review #1241 H1) — `undo-record` cho một khoản đã ĐÓNG BẰNG CẤN (tiền ứng trước — `member_dues.ledgerTxId`
 * là bút toán OFFSET). Huỷ nó đảo OFFSET (khoản về chờ đóng) trong khi phần ứng trước đã cấn có thể đã thành nợ lại quỹ (huỷ ghi nhận lần đóng
 * dư) ⇒ đếm nợ hai lần. Chặn fail-closed, 0 bút toán. CHƯA có lối ra trong app (FIX-1241b B1: sửa nhầm chặn OFFSET — `LEDGER_TX_NOT_CORRECTABLE`);
 * "gỡ cấn" đúng nghĩa là việc sau (lanes §8 UNDO-OFFSET-PAID-DUE). Không `params`.
 */
export const MEMBER_DUE_PAID_BY_OFFSET_CODE = "MEMBER_DUE_PAID_BY_OFFSET" as const;

/**
 * 🔴 FUND-DEBT-SELF-CONFIRM (@UC9264 @UC0057 · §3.7 · QĐ PO 06/10/2026 "Cần người khác xác nhận") — khoản "nợ với quỹ" (đợt `fund_debt`): CHÍNH người
 * nợ (tài khoản đại diện suất giữ khoản) KHÔNG tự ghi nhận / tự duyệt được việc trả của mình — một O/A/T KHÁC phải xác nhận. Ngoại lệ hẹp: nhóm chỉ có
 * ĐÚNG MỘT người quản lý và đó là người nợ ⇒ được tự xác nhận, ghi nhãn `self_one_party` trên bút toán + audit (không trả mã này). 403, không `params`.
 */
export const FUND_DEBT_SELF_CONFIRM_FORBIDDEN_CODE = "FUND_DEBT_SELF_CONFIRM_FORBIDDEN" as const;

/**
 * 🔴 RECORD-PAID-409-CODES (@UC0040 @UC0051) — MÃ ỔN ĐỊNH cho 409 của cửa DUYỆT / TỪ CHỐI lời khai khi lời khai KHÔNG còn chờ
 * (đã được duyệt / từ chối — bởi người khác, hoặc bị đường sửa nhầm huỷ). Trước đây service ném 409 chuỗi trần ⇒ problem+json
 * `code: "CONFLICT"` chung. `params.status` = trạng thái HIỆN TẠI của lời khai (đọc dưới khoá dòng, `PAYMENT_CONFIRMATION_STATUS`).
 * Lời khai không còn tồn tại ⇒ 404 (cùng câu trả lời với `PaymentConfirmationAccessGuard`), không phải mã này.
 */
export const PAYMENT_CONFIRMATION_NOT_PENDING_CODE = "PAYMENT_CONFIRMATION_NOT_PENDING" as const;

/**
 * 🔴 RECORD-PAID-409-CODES (@UC0040 @UC0051) — MÃ ỔN ĐỊNH cho 409 khi bút toán ghi nhận / duyệt của lượt này đụng khoá idempotency mà
 * một yêu cầu KHÁC vừa ghi cho cùng khoản (P2002 trên `unique(fundId, idempotencyKey)`). 0 bút toán của lượt này; dữ liệu đúng —
 * tải lại sẽ thấy kết quả của yêu cầu kia. Không có `params`.
 */
export const MEMBER_DUE_CONCURRENT_WRITE_CODE = "MEMBER_DUE_CONCURRENT_WRITE" as const;

/**
 * 🔴 RECORD-PAID-409-LOWS (@UC0040, review #1163 L1) — MÃ của 404 "dòng `member_dues` BIẾN MẤT dưới khoá" (guard đã thấy khoản, tới lệnh
 * `FOR UPDATE` thì đợt thu vừa bị xoá song song). HTTP vẫn 404 như guard; mã tồn tại để nơi gọi NỘI BỘ phân biệt được với MỌI 404 khác
 * cùng lớp `NotFoundException` — `recordPaidBulk` chỉ bỏ qua đúng mã này; 404 của nguồn khác (vd `FundConfigService` khi quỹ bị xoá mềm
 * giữa lô) phải ném ra, không bị nuốt thành "lô ghi ít hơn". Không `params`.
 */
export const MEMBER_DUE_NOT_FOUND_CODE = "MEMBER_DUE_NOT_FOUND" as const;

/**
 * 🔴 AI-CORE-ERROR-CODES (AI-FLOW-REVIEW-20261015 L2#5 · @UC0040 @UC0061) — MÃ của 403 "người gọi KHÔNG (còn) đại diện suất này" ở các cửa ghi
 * cho CHÍNH suất mình (báo đã chuyển · rút lời báo · trả lời đề nghị đóng hộ). Trợ lý chỉ đưa suất mà phép cấp quyền cùng vị-từ trả về ⇒ chỉ
 * gặp khi liên kết bị gỡ giữa lúc hiện phiếu và lúc bấm. Trước mã này: 403 rỗng / chuỗi trần ⇒ trợ lý "trục trặc, thử lại". HTTP vẫn 403,
 * giữ nguyên `message` cũ (nếu có). Không `params`.
 */
export const SEAT_NOT_REPRESENTED_CODE = "SEAT_NOT_REPRESENTED" as const;

/* ===========================================================================
 * US-E10.F2-0076 — Gateway payment webhook (@UC0057) — @gate money,ledger.
 *
 * Schema GENERIC (provider-agnostic — VNPay/MoMo/ZaloPay adapter thật DEFER). Webhook = UNTRUSTED:
 * body CHỈ được tin SAU khi verify HMAC (constant-time) + timestamp window ở tầng verifier. Sau verify,
 * body parse qua schema này (validate biên C5). amountMinor = STRING minor unit (BigInt, KHÔNG float).
 * ========================================================================= */

/**
 * Payload webhook cổng thanh toán (generic). `.strip()` (mặc định zod) — bỏ field lạ provider gửi kèm
 * (KHÔNG .strict để tương thích nhiều provider), nhưng CÁC field lõi bắt buộc + đúng kiểu:
 *  - eventId: định danh sự kiện gateway — DÙNG LÀM khoá dedupe (idempotency chống replay). Non-empty.
 *  - memberDueId: khoản thu cần khớp (uuid).
 *  - amountMinor: số tiền đã thu (minor unit string) — service SO KHỚP với member_due.amountMinor.
 *  - currency: 3 ký tự — service SO KHỚP currency due.
 *  - paidAt: thời điểm thu (optional datetime — chỉ metadata, KHÔNG dùng cho dedupe/verify).
 */
export const gatewayWebhookRequestSchema = z.object({
  eventId: z.string().trim().min(1).max(200),
  memberDueId: z.string().uuid(),
  amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  currency: z.string().length(3),
  paidAt: z.string().datetime().optional(),
});

export type GatewayWebhookRequest = z.infer<typeof gatewayWebhookRequestSchema>;

/** Response webhook — cố ý TỐI GIẢN + ĐỒNG NHẤT (no-leak): chỉ báo đã nhận. KHÔNG lộ trạng thái ledger. */
export const gatewayWebhookResponseSchema = z
  .object({
    received: z.literal(true),
  })
  .strict();

export type GatewayWebhookResponse = z.infer<typeof gatewayWebhookResponseSchema>;
